import {useState} from "react";
import {Box, Chip, CircularProgress, Paper, Typography} from "@mui/material";
import MySearchField from "../../../components/MySearchField";
import {useAuthUserSearch} from "../../../hooks/useAuthUserSearch";

// Reusable user search + result list for the FE-7 clone (Step 1) and copy-groups (Step 3) flows.
// Searches login / name / email (API-1, GET /api/v3/auth/users?search=). businessUnitCode is optional
// — omitted for the Step-1 clone (BU not chosen yet), supplied for the Step-3 copy (scoped to the BU).
// Each row shows the candidate's name, user type, BU, email, group pills, and (optionally) territory
// count. Selecting a row calls onSelect(row) with the full result object (it already carries group ids).
const rowName = (u) => [u.firstName, u.lastName].filter(Boolean).join(" ") || u.login;

const UserSearchPicker = ({businessUnitCode, onSelect, showTerritoryCount = false}) => {
    const [term, setTerm] = useState("");
    const {users, isLoading, isError} = useAuthUserSearch(term, businessUnitCode);
    const hasTerm = !!term && term.trim().length > 0;

    return (
        <Box>
            <MySearchField label="Search by name, login, or email" value={term} onChange={setTerm} />

            {isLoading && hasTerm && (
                <Box sx={{display: "flex", justifyContent: "center", p: 2}}>
                    <CircularProgress size={20} />
                </Box>
            )}
            {isError && hasTerm && (
                <Typography sx={{color: "error.main", fontSize: "0.85rem", mt: 1}}>
                    Couldn't search users. Please try again.
                </Typography>
            )}
            {!isLoading && !isError && hasTerm && users.length === 0 && (
                <Typography sx={{color: "text.secondary", fontStyle: "italic", fontSize: "0.85rem", mt: 1}}>
                    No users match “{term}”.
                </Typography>
            )}

            <Box sx={{display: "flex", flexDirection: "column", gap: 1, mt: 1, maxHeight: 340, overflowY: "auto"}}>
                {users.map((u) => (
                    <Paper
                        key={u.id}
                        variant="outlined"
                        onClick={() => onSelect(u)}
                        sx={{p: 1.5, cursor: "pointer", "&:hover": {borderColor: "secondary.main", backgroundColor: "rgba(0,163,255,0.04)"}}}
                    >
                        <Box sx={{display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap"}}>
                            <Typography sx={{fontWeight: 700, fontSize: "0.9rem"}}>{rowName(u)}</Typography>
                            <Typography sx={{fontSize: "0.8rem", color: "text.secondary"}}>
                                {u.userType?.displayName || "—"}
                                {u.businessUnitCode ? ` · ${u.businessUnitCode}` : ""}
                            </Typography>
                        </Box>
                        {u.email && (
                            <Typography sx={{fontSize: "0.8rem", color: "text.secondary"}}>{u.email}</Typography>
                        )}
                        {(u.groups ?? []).length > 0 && (
                            <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.75}}>
                                {u.groups.map((g) => (
                                    <Chip key={g.id} size="small" variant="outlined" label={g.name} sx={{fontSize: "0.7rem"}} />
                                ))}
                            </Box>
                        )}
                        {showTerritoryCount && (
                            <Typography sx={{fontSize: "0.72rem", color: "text.secondary", mt: 0.5}}>
                                {u.territoryCount ?? 0} territor{(u.territoryCount ?? 0) === 1 ? "y" : "ies"}
                            </Typography>
                        )}
                    </Paper>
                ))}
            </Box>
        </Box>
    );
};

export default UserSearchPicker;
